import React, { useRef, useEffect } from 'react';
import { List, Typography } from 'antd';
import { RedFlag } from 'types/redFlagTypes';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import dayjs from 'dayjs';
import { DATE_FORMAT, DATE_TIME_AM_PM_FORMAT } from 'constants/dates';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { StopOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface RedFlagDetailsModalProps {
    open: boolean;
    onClose: () => void;
    redFlagData: RedFlag | null;
}

const RedFlagDetailsModal: React.FC<RedFlagDetailsModalProps> = ({ open, onClose, redFlagData }) => {
    const { redFlagLogs } = useSelector((state: RootState) => state.redFlags);
    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (open && scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTop = 0;
        }
    }, [open]);

    // Sort logs in descending order by timestamp
    const sortedLogs = redFlagLogs.slice().sort((a, b) => {
        if (a.timestamp && b.timestamp) {
            return dayjs(b.timestamp).diff(dayjs(a.timestamp));
        }
        return 0;
    });

    const renderListItem = (label: string, value: any) => {
        if (value !== null && value !== undefined) {
            return (
                <List.Item>
                    <Text strong>{label}:</Text> {value}
                </List.Item>
            );
        }
        return null;
    };

    const getLevelNameDisplay = (levelName: string) => {
        if (levelName === 'Blocklist') {
            return (
                <span>
                    <StopOutlined style={{ color: 'rgba(242,103,87,1.0)' }} /> {t('redFlag.form.blocklist')}
                </span>
            );
        } else if (levelName === 'Watchlist') {
            return (
                <span>
                    <EyeOutlined style={{ color: '#FFD54F' }} /> {t('redFlag.form.watchlist')}
                </span>
            );
        } else {
            return levelName;
        }
    };

    const profileData = [
        { label: t('visitors.redFlag.modal.fields.type'), value: redFlagData ? getLevelNameDisplay(redFlagData.levelName!) : 'N/A' },
        { label: t('visitors.redFlag.modal.fields.firstName'), value: redFlagData?.firstName },
        { label: t('visitors.redFlag.modal.fields.lastName'), value: redFlagData?.lastName },
        { label: t('visitors.redFlag.modal.fields.middleName'), value: redFlagData?.middleName },
    ].filter(item => item.value);

    const dateData = [
        { label: t('visitors.redFlag.modal.fields.effectiveDate'), value: redFlagData?.effectiveDate },
        { label: t('visitors.redFlag.modal.fields.expireDate'), value: dayjs(redFlagData?.expireDate).format(DATE_FORMAT) },
    ].filter(item => dayjs(item.value).isValid());

    const dobData = [
        { label: t('visitors.redFlag.modal.fields.dob'), value: dayjs(redFlagData?.dateOfBirth).format(DATE_FORMAT) },
    ].filter(item => dayjs(item.value).isValid());

    return (
        <OpModal
            title={t('visitors.redFlag.modal.title')}
            open={open}
            onOk={onClose}
            onCancel={onClose}
            okText={t('visitors.redFlag.modal.close')}
            cancelButtonProps={{ style: { display: 'none' } }}
        >
            <div ref={scrollableContainerRef} style={{ overflowY: 'auto', maxHeight: '50vh' }}>
                {profileData.length > 0 && (
                    <List
                        bordered
                        dataSource={profileData}
                        renderItem={(item) => renderListItem(item.label, item.value)}
                    />
                )}
                {profileData.length > 0 && <div style={{ margin: '16px 0' }} />}
                {dateData.length > 0 && (
                    <List
                        bordered
                        dataSource={dateData}
                        renderItem={(item) => renderListItem(item.label, item.value)}
                    />
                )}
                {dateData.length > 0 && <div style={{ margin: '16px 0' }} />}
                {dobData.length > 0 && (
                    <List
                        bordered
                        dataSource={dobData}
                        renderItem={(item) => renderListItem(item.label, item.value)}
                    />
                )}
                {redFlagData && redFlagData.data && redFlagData.data.length > 0 && (
                    <>
                        <div style={{ margin: '16px 0' }} />
                        <List
                            bordered
                            header={<div style={{ color: 'var(--colorPrimary)' }}>{t('visitors.redFlag.modal.fields.additional')}</div>}
                            dataSource={redFlagData.data}
                            renderItem={(item) => (
                                <List.Item>
                                    <Text strong>{item.data}:</Text> {item.value}
                                </List.Item>
                            )}
                        />
                    </>
                )}
                {sortedLogs.length > 0 && (
                    <>
                        <div style={{ margin: '16px 0' }} />
                        <List
                            bordered
                            header={<div style={{ color: 'var(--colorPrimary)' }}>{t('visitors.redFlag.modal.fields.logs')}</div>}
                            dataSource={sortedLogs}
                            renderItem={(item) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={item.log}
                                        description={dayjs(item.timestamp).utc(true).local().format(DATE_TIME_AM_PM_FORMAT)}
                                    />
                                </List.Item>
                            )}
                        />
                    </>
                )}
            </div>
        </OpModal>
    );
};

export default RedFlagDetailsModal;
